import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader, PageContainer } from '@/components/layout/PageHeader'
import { Button, Badge, Card, CardContent } from '@/components/ui'
import { adminApi } from '@/modules/admin/api/admin.api'

type SettingsSection = 'general' | 'security' | 'notifications' | 'integrations' | 'workflows'

const SECTIONS: { key: SettingsSection; label: string; icon: string }[] = [
  { key: 'general', label: 'Général', icon: '⚙️' },
  { key: 'security', label: 'Sécurité', icon: '🔒' },
  { key: 'notifications', label: 'Notifications', icon: '🔔' },
  { key: 'workflows', label: 'Workflows', icon: '🔄' },
  { key: 'integrations', label: 'Intégrations', icon: '🔗' },
]

export default function SettingsPage() {
  const [activeSection, setActiveSection] = useState<SettingsSection>('general')
  const queryClient = useQueryClient()
  const [saved, setSaved] = useState(false)

  const { data: config } = useQuery({
    queryKey: ['app-config'],
    queryFn: () => adminApi.getAppConfig(),
  })

  const updateConfig = useMutation({
    mutationFn: (data: Record<string, any>) => adminApi.updateAppConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-config'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  return (
    <PageContainer>
      <PageHeader
        title="Paramètres"
        description="Configuration globale de NexusCorp ERP"
        breadcrumb={[{ label: 'Administration' }, { label: 'Paramètres' }]}
      />

      <div className="flex gap-6">
        {/* Sidebar navigation */}
        <nav className="w-48 flex-shrink-0">
          <div className="space-y-1">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                  activeSection === s.key
                    ? 'bg-[var(--blue-bg)] text-[var(--blue-d)] font-medium'
                    : 'text-[var(--tx3)] hover:text-[var(--tx)] hover:bg-white'
                }`}
              >
                <span>{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Contenu */}
        <div className="flex-1 space-y-4">
          {saved && (
            <div className="bg-[var(--green-bg)] border border-[rgba(16,185,129,0.25)] rounded-xl p-3 text-sm text-[var(--green-text)]">
              ✅ Paramètres enregistrés avec succès.
            </div>
          )}

          {/* Général */}
          {activeSection === 'general' && (
            <SettingsGeneral config={config} onSave={(d) => updateConfig.mutate(d)} saving={updateConfig.isPending} />
          )}

          {/* Sécurité */}
          {activeSection === 'security' && (
            <SettingsSecurity config={config} onSave={(d) => updateConfig.mutate(d)} saving={updateConfig.isPending} />
          )}

          {/* Notifications */}
          {activeSection === 'notifications' && (
            <SettingsNotifications config={config} onSave={(d) => updateConfig.mutate(d)} saving={updateConfig.isPending} />
          )}

          {/* Intégrations */}
          {activeSection === 'integrations' && (
            <SettingsIntegrations />
          )}

          {/* Workflows NexusCorp (M12) */}
          {activeSection === 'workflows' && (
            <SettingsWorkflows config={config} onSave={(d) => updateConfig.mutate(d)} saving={updateConfig.isPending} />
          )}
        </div>
      </div>
    </PageContainer>
  )
}

// ── Section Général ────────────────────────────────────────────────────────────

function SettingsGeneral({ config, onSave, saving }: { config: any; onSave: (d: any) => void; saving: boolean }) {
  const [appName, setAppName] = useState(config?.appName ?? 'NexusCorp ERP')
  const [defaultLang, setDefaultLang] = useState(config?.defaultLang ?? 'fr')
  const [timezone, setTimezone] = useState(config?.timezone ?? 'Africa/Dakar')
  const [dateFormat, setDateFormat] = useState(config?.dateFormat ?? 'DD/MM/YYYY')

  return (
    <Card>
      <CardContent className="py-5">
        <h3 className="text-sm font-semibold text-[var(--tx2)] mb-5">Paramètres généraux</h3>
        <div className="space-y-4">
          <Field label="Nom de l'application">
            <input value={appName} onChange={(e) => setAppName(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Langue par défaut">
            <select value={defaultLang} onChange={(e) => setDefaultLang(e.target.value)} className={inputCls}>
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="wo">Wolof</option>
            </select>
          </Field>
          <Field label="Fuseau horaire">
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={inputCls}>
              <option value="Africa/Dakar">Africa/Dakar (UTC+0)</option>
              <option value="Europe/Paris">Europe/Paris (UTC+1/+2)</option>
              <option value="UTC">UTC</option>
            </select>
          </Field>
          <Field label="Format de date">
            <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} className={inputCls}>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
            </select>
          </Field>
        </div>
        <div className="mt-6 pt-4 border-t border-[var(--bd)]">
          <Button onClick={() => onSave({ appName, defaultLang, timezone, dateFormat })} isLoading={saving} size="sm">
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Section Sécurité ───────────────────────────────────────────────────────────

function SettingsSecurity({ config, onSave, saving }: { config: any; onSave: (d: any) => void; saving: boolean }) {
  const [maxLoginAttempts, setMaxLoginAttempts] = useState(config?.maxLoginAttempts ?? 5)
  const [lockoutDuration, setLockoutDuration] = useState(config?.lockoutDurationMinutes ?? 15)
  const [sessionTimeout, setSessionTimeout] = useState(config?.sessionTimeoutMinutes ?? 60)
  const [require2fa, setRequire2fa] = useState(config?.require2fa ?? false)
  const [passwordMinLength, setPasswordMinLength] = useState(config?.passwordMinLength ?? 8)

  return (
    <Card>
      <CardContent className="py-5">
        <h3 className="text-sm font-semibold text-[var(--tx2)] mb-5">Sécurité & Authentification</h3>
        <div className="space-y-4">
          <Field label="Tentatives de connexion max" hint="Avant verrouillage du compte">
            <input type="number" min={3} max={20} value={maxLoginAttempts}
              onChange={(e) => setMaxLoginAttempts(+e.target.value)} className={inputCls} />
          </Field>
          <Field label="Durée de verrouillage (minutes)">
            <input type="number" min={5} max={1440} value={lockoutDuration}
              onChange={(e) => setLockoutDuration(+e.target.value)} className={inputCls} />
          </Field>
          <Field label="Expiration de session (minutes)">
            <input type="number" min={15} max={480} value={sessionTimeout}
              onChange={(e) => setSessionTimeout(+e.target.value)} className={inputCls} />
          </Field>
          <Field label="Longueur mot de passe minimum">
            <input type="number" min={8} max={32} value={passwordMinLength}
              onChange={(e) => setPasswordMinLength(+e.target.value)} className={inputCls} />
          </Field>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-[var(--tx2)]">Exiger 2FA pour tous les utilisateurs</p>
              <p className="text-xs text-[var(--tx3)] mt-0.5">Force l'activation de l'authentification à deux facteurs</p>
            </div>
            <button
              onClick={() => setRequire2fa(!require2fa)}
              className={`relative w-11 h-6 rounded-full transition-colors ${require2fa ? 'bg-[var(--blue)]' : 'bg-[var(--surface-2)]'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${require2fa ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-[var(--bd)]">
          <Button onClick={() => onSave({ maxLoginAttempts, lockoutDurationMinutes: lockoutDuration, sessionTimeoutMinutes: sessionTimeout, require2fa, passwordMinLength })} isLoading={saving} size="sm">
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Section Notifications ──────────────────────────────────────────────────────

function SettingsNotifications({ config, onSave, saving }: { config: any; onSave: (d: any) => void; saving: boolean }) {
  const [emailEnabled, setEmailEnabled] = useState(config?.emailNotifications ?? true)
  const [smtpHost, setSmtpHost] = useState(config?.smtpHost ?? '')
  const [smtpPort, setSmtpPort] = useState(config?.smtpPort ?? 587)
  const [smtpUser, setSmtpUser] = useState(config?.smtpUser ?? '')
  const [emailFrom, setEmailFrom] = useState(config?.emailFrom ?? 'noreply@nexuscorp.sn')

  return (
    <Card>
      <CardContent className="py-5">
        <h3 className="text-sm font-semibold text-[var(--tx2)] mb-5">Configuration Email (SMTP)</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-[var(--tx2)]">Notifications email activées</p>
            </div>
            <button
              onClick={() => setEmailEnabled(!emailEnabled)}
              className={`relative w-11 h-6 rounded-full transition-colors ${emailEnabled ? 'bg-[var(--blue)]' : 'bg-[var(--surface-2)]'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${emailEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <Field label="Serveur SMTP">
            <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.example.com" className={inputCls} disabled={!emailEnabled} />
          </Field>
          <Field label="Port SMTP">
            <input type="number" value={smtpPort} onChange={(e) => setSmtpPort(+e.target.value)} className={inputCls} disabled={!emailEnabled} />
          </Field>
          <Field label="Utilisateur SMTP">
            <input value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} placeholder="user@example.com" className={inputCls} disabled={!emailEnabled} />
          </Field>
          <Field label="Expéditeur (From)">
            <input value={emailFrom} onChange={(e) => setEmailFrom(e.target.value)} placeholder="noreply@nexuscorp.sn" className={inputCls} disabled={!emailEnabled} />
          </Field>
        </div>
        <div className="mt-6 pt-4 border-t border-[var(--bd)]">
          <Button onClick={() => onSave({ emailNotifications: emailEnabled, smtpHost, smtpPort, smtpUser, emailFrom })} isLoading={saving} size="sm">
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Section Intégrations ───────────────────────────────────────────────────────

function SettingsIntegrations() {
  const integrations = [
    { name: 'MinIO (Stockage)', description: 'Stockage de fichiers et documents', status: 'configured', icon: '🗄️' },
    { name: 'Redis (Cache)', description: 'Cache sessions et queues Bull', status: 'configured', icon: '⚡' },
    { name: 'SMTP Email', description: 'Envoi d\'emails transactionnels', status: 'pending', icon: '📧' },
    { name: 'Twilio SMS', description: 'Notifications SMS (Phase 3)', status: 'disabled', icon: '📱' },
    { name: 'Wave / Orange Money', description: 'Paiements mobile money (Phase 5)', status: 'disabled', icon: '💳' },
  ]

  const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'default' }> = {
    configured: { label: 'Configuré', variant: 'success' },
    pending: { label: 'À configurer', variant: 'warning' },
    disabled: { label: 'Non activé', variant: 'default' },
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-5 py-4 border-b border-[var(--bd)]">
          <h3 className="text-sm font-semibold text-[var(--tx2)]">Intégrations & Services</h3>
        </div>
        <div className="divide-y divide-[var(--bd)]">
          {integrations.map((integration) => {
            const s = statusConfig[integration.status]
            return (
              <div key={integration.name} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{integration.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-[var(--tx)]">{integration.name}</p>
                    <p className="text-xs text-[var(--tx3)] mt-0.5">{integration.description}</p>
                  </div>
                </div>
                <Badge variant={s.variant} size="sm">{s.label}</Badge>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const inputCls = 'w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500 disabled:opacity-40 disabled:cursor-not-allowed'

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--tx3)] mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-[var(--tx3)] mt-1">{hint}</p>}
    </div>
  )
}

// ── Section Workflows NexusCorp (M12) ───────────────────────────────────────────

function SettingsWorkflows({ config, onSave, saving }: { config: any; onSave: (d: any) => void; saving: boolean }) {
  const c = config ?? {}
  const [f, setF] = useState({
    'workflow.leave.requireManagerApproval': c['workflow.leave.requireManagerApproval'] ?? true,
    'workflow.leave.requireHrApproval': c['workflow.leave.requireHrApproval'] ?? true,
    'workflow.leave.autoApproveUnder': String(c['workflow.leave.autoApproveUnder'] ?? 0),
    'workflow.exitVoucher.requireSignature': c['workflow.exitVoucher.requireSignature'] ?? true,
    'workflow.exitVoucher.maxWithoutApproval': String(c['workflow.exitVoucher.maxWithoutApproval'] ?? 50000),
    'workflow.commission.stages': c['workflow.commission.stages'] ?? 'MANAGER_APPROVED,HR_APPROVED,FINANCE_APPROVED',
    'workflow.terrain.autoCompleteOnReports': c['workflow.terrain.autoCompleteOnReports'] ?? false,
    'workflow.terrain.requirePhotoReport': c['workflow.terrain.requirePhotoReport'] ?? true,
    'workflow.attendance.requireSelfie': c['workflow.attendance.requireSelfie'] ?? false,
    'workflow.attendance.requireGeoloc': c['workflow.attendance.requireGeoloc'] ?? true,
    'workflow.attendance.geolocRadiusMeters': String(c['workflow.attendance.geolocRadiusMeters'] ?? 200),
    'workflow.stock.lowStockThreshold': String(c['workflow.stock.lowStockThreshold'] ?? 10),
    'workflow.stock.autoReorderEnabled': c['workflow.stock.autoReorderEnabled'] ?? false,
  })

  const set = (k: string, v: any) => setF(p => ({ ...p, [k]: v }))

  const handleSave = () => {
    const payload: Record<string, any> = {}
    for (const [k, v] of Object.entries(f)) {
      if (typeof v === 'boolean') payload[k] = v
      else if (!isNaN(Number(v)) && v !== '') payload[k] = Number(v)
      else payload[k] = v
    }
    onSave(payload)
  }

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Congés */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--tx)]">🏖️ Congés & Absences</h3>
          <Toggle label="Approbation manager requise" value={f['workflow.leave.requireManagerApproval'] as boolean} onChange={v => set('workflow.leave.requireManagerApproval', v)} hint="Le manager direct doit approuver avant le RH" />
          <Toggle label="Approbation RH requise" value={f['workflow.leave.requireHrApproval'] as boolean} onChange={v => set('workflow.leave.requireHrApproval', v)} hint="Le service RH doit valider la demande" />
          <Field label="Auto-approbation si ≤ X jours" hint="0 = désactivé. Les demandes courtes sont auto-approuvées">
            <input type="number" min="0" className={inputCls} value={f['workflow.leave.autoApproveUnder']} onChange={e => set('workflow.leave.autoApproveUnder', e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      {/* Bons de sortie */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--tx)]">📦 Bons de Sortie Stock</h3>
          <Toggle label="Signature obligatoire" value={f['workflow.exitVoucher.requireSignature'] as boolean} onChange={v => set('workflow.exitVoucher.requireSignature', v)} hint="Le réceptionnaire doit signer le bon" />
          <Field label="Montant max sans approbation (XOF)" hint="Au-delà de ce montant, une approbation manager est requise">
            <input type="number" min="0" className={inputCls} value={f['workflow.exitVoucher.maxWithoutApproval']} onChange={e => set('workflow.exitVoucher.maxWithoutApproval', e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      {/* Commissions */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--tx)]">💰 Commissions CRM</h3>
          <Field label="Étapes d'approbation" hint="Séparées par des virgules. Ex: MANAGER_APPROVED,HR_APPROVED,FINANCE_APPROVED">
            <input type="text" className={inputCls} value={f['workflow.commission.stages'] as string} onChange={e => set('workflow.commission.stages', e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      {/* Terrain */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--tx)]">🗺️ Missions Terrain</h3>
          <Toggle label="Auto-compléter quand tous les rapports sont soumis" value={f['workflow.terrain.autoCompleteOnReports'] as boolean} onChange={v => set('workflow.terrain.autoCompleteOnReports', v)} hint="La mission passe en COMPLETED automatiquement" />
          <Toggle label="Photo obligatoire dans les rapports" value={f['workflow.terrain.requirePhotoReport'] as boolean} onChange={v => set('workflow.terrain.requirePhotoReport', v)} hint="Les rapports sans photo seront marqués comme incomplets" />
        </CardContent>
      </Card>

      {/* Pointage */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--tx)]">📷 Pointage Mobile</h3>
          <Toggle label="Selfie obligatoire" value={f['workflow.attendance.requireSelfie'] as boolean} onChange={v => set('workflow.attendance.requireSelfie', v)} hint="L'employé doit prendre un selfie pour valider le pointage" />
          <Toggle label="Géolocalisation obligatoire" value={f['workflow.attendance.requireGeoloc'] as boolean} onChange={v => set('workflow.attendance.requireGeoloc', v)} hint="Le pointage est rejeté si la position est trop éloignée du site" />
          <Field label="Rayon de validation (mètres)" hint="Distance max entre l'employé et le site de pointage">
            <input type="number" min="10" max="5000" className={inputCls} value={f['workflow.attendance.geolocRadiusMeters']} onChange={e => set('workflow.attendance.geolocRadiusMeters', e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      {/* Stock */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--tx)]">📊 Stock & Matériel</h3>
          <Field label="Seuil alerte stock bas" hint="Nombre d'unités en dessous duquel l'alerte se déclenche">
            <input type="number" min="0" className={inputCls} value={f['workflow.stock.lowStockThreshold']} onChange={e => set('workflow.stock.lowStockThreshold', e.target.value)} />
          </Field>
          <Toggle label="Réapprovisionnement automatique" value={f['workflow.stock.autoReorderEnabled'] as boolean} onChange={v => set('workflow.stock.autoReorderEnabled', v)} hint="Créer automatiquement un bon de commande quand le stock est bas" />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} isLoading={saving}>Enregistrer les workflows</Button>
      </div>
    </div>
  )
}

function Toggle({ label, value, onChange, hint }: { label: string; value: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <span className="text-sm text-[var(--tx)]">{label}</span>
        {hint && <p className="text-xs text-[var(--tx3)] mt-0.5">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${value ? 'bg-[var(--blue)]' : 'bg-gray-200'}`}
      >
        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  )
}
